/**
 * Push notifications and message preferences (Phase 25). Push uses the
 * standard Web Push API with the server's VAPID public key: Chrome, Edge,
 * Firefox, Safari 16+ on Mac, Android, and iOS 16.4+ once Rankbist is added
 * to the Home Screen.
 */
import { getToken } from "./api";
import { errorFromResponse, networkError, readJson } from "./httpError";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "http://localhost:8000";

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  } catch {
    throw networkError();
  }
  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  return readJson<T>(res);
}

export interface PushConfig {
  enabled: boolean;
  public_key: string | null;
  devices: number;
}

export interface MessagePrefs {
  marketing_email: boolean;
  product_email: boolean;
  push_marketing: boolean;
  email: string | null;
}

export type PushState = "unsupported" | "needs-install" | "unavailable" | "blocked" | "off" | "on";

export const messagesApi = {
  pushConfig: () => call<PushConfig>("/api/push/config"),
  prefs: () => call<MessagePrefs>("/api/me/message-prefs"),
  savePrefs: (p: Partial<MessagePrefs>) => call<MessagePrefs>("/api/me/message-prefs", { method: "PUT", body: JSON.stringify(p) }),
  prefsByLink: (token: string) => call<MessagePrefs>(`/api/messages/prefs/${encodeURIComponent(token)}`),
  savePrefsByLink: (token: string, p: Partial<MessagePrefs>) =>
    call<MessagePrefs>(`/api/messages/prefs/${encodeURIComponent(token)}`, { method: "PUT", body: JSON.stringify(p) }),
};

export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function supported(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** The service worker, registering it if needed (push needs one, even in development). */
export async function serviceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration("/");
  if (existing) return existing;
  await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  return navigator.serviceWorker.ready;
}

function keyBytes(base64: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Where push stands on this device. */
export async function pushState(config: PushConfig | null): Promise<PushState> {
  if (!supported()) return isIos() && !isStandalone() ? "needs-install" : "unsupported";
  if (!config?.enabled || !config.public_key) return "unavailable";
  if (Notification.permission === "denied") return "blocked";
  const reg = await navigator.serviceWorker.getRegistration("/");
  const sub = await reg?.pushManager.getSubscription();
  return sub && Notification.permission === "granted" ? "on" : "off";
}

/** Asks permission, subscribes this browser and tells the server. */
export async function enablePush(config: PushConfig): Promise<PushState> {
  if (!supported() || !config.public_key) return "unsupported";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "blocked" : "off";
  const reg = await serviceWorker();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(config.public_key) });
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await call("/api/push/subscribe", { method: "POST", body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys }) });
  return "on";
}

/** Unsubscribes this browser only (other devices keep their notifications). */
export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration("/");
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await call("/api/push/unsubscribe", { method: "POST", body: JSON.stringify({ endpoint: sub.endpoint }) }).catch(() => undefined);
    await sub.unsubscribe();
  }
  return "off";
}
